import { Component, OnInit, inject, signal, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { AnnouncementService } from '../../../core/services/announcement.service';
import { ProfileService } from '../../../core/services/profile.service';
import { AuthService } from '../../../core/auth/auth.service';
import { Announcement, AnnouncementStatus, VIP_PACKAGES, LookupValue } from '../../../core/models/announcement.model';
import { ListingGalleryComponent } from './components/listing-gallery.component';
import { ListingMainInfoComponent } from './components/listing-main-info.component';
import { ContactCardComponent } from './components/contact-card.component';
import { VipUpgradeModalComponent } from './components/vip-upgrade-modal.component';
import { ShareButtonsComponent } from './components/share-buttons.component';
import { ComplaintModalComponent } from './components/complaint-modal.component';
import { ListingDescriptionComponent } from './components/listing-description.component';
import { ListingMapComponent } from './components/listing-map.component';
import { SimilarListingsComponent } from './components/similar-listings.component';
import { StatusBannerComponent } from './components/status-banner.component';
import { InactiveListingComponent } from './components/inactive-listing.component';
import { map, finalize, Observable } from 'rxjs';

@Component({
  selector: 'app-announcement-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ListingGalleryComponent,
    ListingMainInfoComponent,
    ContactCardComponent,
    VipUpgradeModalComponent,
    ShareButtonsComponent,
    ComplaintModalComponent,
    ListingDescriptionComponent,
    ListingMapComponent,
    SimilarListingsComponent,
    StatusBannerComponent,
    InactiveListingComponent
  ],
  templateUrl: './announcement-detail.component.html',
  styleUrls: ['./announcement-detail.component.scss']
})
export class AnnouncementDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private announcementService = inject(AnnouncementService);
  private profileService = inject(ProfileService);
  private authService = inject(AuthService);
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private platformId = inject(PLATFORM_ID);

  listing = signal<Announcement | null>(null);
  similarListings = signal<Announcement[]>([]);
  isLoading = signal<boolean>(true);
  error = signal<string | null>(null);

  isOwner = signal<boolean>(false);
  isAuthenticated = signal<boolean>(false);

  showVipModal = signal<boolean>(false);
  showComplaintModal = signal<boolean>(false);
  vipPackages = VIP_PACKAGES;

  ngOnInit(): void {
    this.isAuthenticated.set(this.authService.isAuthenticated());

    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.loadListing(id);
      }
    });
  }

  loadListing(id: string): void {
    this.isLoading.set(true);
    this.error.set(null);

    // Əgər istifadəçi öz elanını profil vasitəsilə görmək istəyirsə (id listings/:id və ya my-ads-dan gəlib)
    // lakin burada ən etibarlı yol url-i yoxlamaqdır
    const isProfileView = this.router.url.includes('/listings/') && this.authService.isAuthenticated();

    const request$: Observable<Announcement | null | undefined> = isProfileView
      ? this.profileService.getListingById(id).pipe(
          map(response => {
            if (response && response.success && response.data) {
              const data = response.data;
              return {
                ...data,
                rooms: data.roomCount ?? 0,
                area_m2: data.areaSquareMeters ?? 0,
                total_floors: data.totalFloors ?? 0,
                hasExtract: data.hasDocument,
                hasMortgage: data.isMortgageAvailable,
                lat: data.latitude,
                lng: data.longitude,
                imageUrls: (data.images && Array.isArray(data.images))
                  ? data.images.map((img: any) => img.url || img)
                  : (data.imageUrls || []),
                status: this.mapStatus(data.listingModerationStatus)
              } as Announcement;
            }
            return null;
          })
        )
      : this.announcementService.getAnnouncementById(id);

    request$
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (data: Announcement | null | undefined) => {
          if (data) {
            this.listing.set(data);
            this.isOwner.set(this.authService.isListingOwner(data) || isProfileView);
            this.updateMetaTags(data);
            this.loadSimilarListings(id);
          } else {
            this.error.set('Elan tapılmadı');
          }
        },
        error: () => {
          this.error.set('Məlumat yüklənərkən xəta baş verdi');
        }
      });
  }

  loadSimilarListings(id: string): void {
    this.announcementService.getSimilarListings(id).subscribe(data => {
      this.similarListings.set(data);
    });
  }

  updateMetaTags(listing: Announcement): void {
    const title = `${listing.price} ₼ - ${listing.rooms || (this.isLookup(listing.roomCount) ? listing.roomCount.name : (listing.roomCount || ''))} otaqlı - ${listing.region || (this.isLookup(listing.administrativeArea) ? listing.administrativeArea.name : '')}`;
    const description = `${listing.area_m2 || listing.areaSquareMeters || (listing.landAreaSot ? listing.landAreaSot * 100 : '')} m², ${listing.repair || (this.isLookup(listing.renovationStatus) ? listing.renovationStatus.name : '')}, çıxarış ${(listing.hasExtract ?? listing.hasDocument) ? 'var' : 'yoxdur'}`;
    const image = listing.imageUrls?.[0] || 'assets/no-image.jpg';

    this.titleService.setTitle(title);

    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({ property: 'og:description', content: description });
    this.metaService.updateTag({ property: 'og:image', content: image });

    if (isPlatformBrowser(this.platformId)) {
      this.metaService.updateTag({ property: 'og:url', content: window.location.href });
    }
  }

  get galleryImages(): string[] {
    const l = this.listing();
    if (!l) return [];
    return l.imageUrls && l.imageUrls.length > 0 ? l.imageUrls : ['assets/no-image.jpg'];
  }

  get canSeeDetails(): boolean {
    const l = this.listing();
    if (!l) return false;
    // Public API-dən status göndərilmirsə, elanı aktiv kimi qəbul edirik
    if (!l.status || l.status === AnnouncementStatus.Active) return true;
    return this.isOwner();
  }

  onVipSuccess(): void {
    this.showVipModal.set(false);
    this.loadListing(this.listing()!.id.toString());
  }

  mapStatus(statusNum: number): AnnouncementStatus {
    switch (statusNum) {
      case 1: return AnnouncementStatus.Pending;
      case 2: return AnnouncementStatus.Active;
      case 3: return AnnouncementStatus.Rejected;
      case 4: return AnnouncementStatus.Paused;
      default: return AnnouncementStatus.Active;
    }
  }

  isLookup(val: any): val is LookupValue {
    return val && typeof val === 'object' && 'name' in val;
  }
}
